import React, { useEffect, useMemo, useState } from 'react';
import {
  buildPartnerSubdomainUrl,
  formatPartnerReferralDomain,
} from '../../utils/partnerSubdomain';
import {
  buildPartnerBrandingPayload,
  getPartnerProgramFaqsForForm,
  getPartnerProgramServicesForForm,
} from '../../utils/partnerBranding';
import PartnerLogoUpload from '../../components/PartnerLogoUpload';
import PartnerProgramServicesEditor from '../../components/PartnerProgramServicesEditor';
import type { PartnerProgramServiceFormValue } from '../../types/partnerProgramService';
import PartnerProgramFaqsEditor from '../../components/PartnerProgramFaqsEditor';
import type { PartnerProgramFaqFormValue } from '../../types/partnerProgramFaq';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  EditOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Check,
  CircleCheckBig,
  CircleX,
  Copy,
  Globe,
  Briefcase,
  MapPin,
  Home,
  Building2,
  Map,
  Flag,
  Hash,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import callApi from '../../Api/config';
import { AFFILIATE_PARTNERS } from '../../constants/EntityName';
import Accordian from '../../components/Accordian/Accordian';
import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import HorizontalTabNavigation, {
  HorizontalTabPanel,
} from '../../components/HorizontalTabNavigation/HorizontalTabNavigation';
import ConfirmationModal from '../../components/ConfirmationModal';
import {
  joinDialAndLocal,
  PHONE_DIAL_CODES,
  splitDialAndLocal,
} from '../../constants/phoneDialCodes';
import UserFormSidebar from '../../components/UserFormSidebar';
import { InvertFillButton } from '../../components/InvertFillButton';
import { toastError, toastSuccess, toastWarning } from '../../service/Toast';
import '../../components/style.scss';

const { Title, Text } = Typography;

export type AffiliatePartner = {
  id: string;
  partnerId: string;
  name: string;
  companyName?: string;
  websiteUrl?: string;
  industry?: string;
  email: string;
  mobile: string;
  country?: string;
  referralCode: string;
  referralLink: string;
  subdomain?: string;
  branding?: {
    logos?: {
      partner?: string;
    };
    partnerProgramServices?: Array<{
      id: string;
      title: string;
      tag?: string;
      image: string;
      href?: string;
      iconKey?: string;
      isActive?: boolean;
    }>;
    /** @deprecated Use partnerProgramServices */
    popularServices?: Array<{
      id: string;
      title: string;
      tag?: string;
      image: string;
      href?: string;
      iconKey?: string;
      isActive?: boolean;
    }>;
    partnerProgramFaqs?: Array<{
      id: string;
      question: string;
      answer: string;
      sortOrder?: number;
      isActive?: boolean;
    }>;
  };
  commissionType: 'percentage' | 'flat';
  commissionValue: number;
  status: 'active' | 'inactive';
  totalOrders: number;
  totalEarnings: number;
  createdAt: string;
};

type PartnerOrderRow = {
  id: string;
  orderId?: string;
  userName?: string;
  userEmail?: string;
  serviceName?: string;
  paymentStatus?: string;
  commission?: number;
  createdAt?: string;
};

type PartnerDashboardPayload = {
  partner?: { partnerId?: string; totalOrders?: number; totalEarnings?: number };
  stats?: {
    totalOrders?: number;
    totalEarnings?: number;
    activeReferrals?: number;
  };
  orders?: PartnerOrderRow[];
};

type AddressForm = {
  street?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

type PaymentForm = {
  zelle?: string;
  venmo?: string;
  upi?: string;
  bankBeneficiaryName?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankIfscSwift?: string;
};

type CreatePartnerFormValues = {
  name: string;
  companyName?: string;
  websiteUrl?: string;
  industry?: string;
  email: string;
  phoneDialCode: string;
  mobileNumber: string;
  country?: string;
  status: 'active' | 'inactive';
  commissionType: 'percentage' | 'flat';
  commissionValue: number;
  referralLink?: string;
  partnerLogoUrl?: string;
  partnerProgramServices?: PartnerProgramServiceFormValue[];
  partnerProgramFaqs?: PartnerProgramFaqFormValue[];
  address: AddressForm;
  payment: PaymentForm;
};

export type B2BAffiliatePartnerEditTabProps = {
  initialPartner: AffiliatePartner;
  countries: string[];
  onClose: () => void;
  onSaved: () => void;
};

const B2BAffiliatePartnerEditTab = ({
  initialPartner,
  countries,
  onClose,
  onSaved,
}: B2BAffiliatePartnerEditTabProps) => {
  const [form] = Form.useForm<CreatePartnerFormValues>();
  const commissionTypeWatched = Form.useWatch('commissionType', form);
  const nameWatched = Form.useWatch('name', form);
  const companyNameWatched = Form.useWatch('companyName', form);
  const countryWatched = Form.useWatch('country', form);
  const emailWatched = Form.useWatch('email', form);
  const phoneDialCodeWatched = Form.useWatch('phoneDialCode', form);
  const mobileNumberWatched = Form.useWatch('mobileNumber', form);
  const statusWatched = Form.useWatch('status', form);
  const websiteUrlWatched = Form.useWatch('websiteUrl', form);
  const industryWatched = Form.useWatch('industry', form);
  const addressWatched = Form.useWatch('address', form);
  const [editingPartner, setEditingPartner] = useState<AffiliatePartner>(initialPartner);
  const partnerAddress = (editingPartner as any).address || {};
  const [submitting, setSubmitting] = useState(false);
  const [partnerDashboard, setPartnerDashboard] = useState<PartnerDashboardPayload | null>(null);
  const [partnerDashboardLoading, setPartnerDashboardLoading] = useState(false);
  const [partnerKyc, setPartnerKyc] = useState<any>(null);
  const [partnerKycLoading, setPartnerKycLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewRemark, setPreviewRemark] = useState('');
  const [partnerInformationEditing, setPartnerInformationEditing] = useState(false);

  /** Order table filters (same pattern as B2B referral orders UI). */
  const [orderSearch, setOrderSearch] = useState('');
  const [filterService, setFilterService] = useState<string>('__all__');
  const [filterPayment, setFilterPayment] = useState<string>('__all__');
  const [filterDatePreset, setFilterDatePreset] = useState<string>('all');

  const fetchPartnerKyc = async (partnerId: string) => {
    setPartnerKycLoading(true);
    try {
      const res: any = await callApi(
        `/affiliate/partners/${encodeURIComponent(partnerId)}/kyc`,
        'GET'
      );
      if (res?.status) {
        setPartnerKyc(res?.data?.data || null);
      } else {
        setPartnerKyc(null);
      }
    } catch {
      setPartnerKyc(null);
    } finally {
      setPartnerKycLoading(false);
    }
  };

  const fetchPartnerDashboard = async (partnerId: string) => {
    if (!partnerId) {
      setPartnerDashboard(null);
      return;
    }
    setPartnerDashboardLoading(true);
    try {
      const res: any = await callApi('/affiliate/partner/dashboard', 'GET', null, null, {
        'x-partner-id': partnerId,
      });
      if (res?.status) {
        setPartnerDashboard(res?.data?.data || null);
      } else {
        setPartnerDashboard(null);
      }
    } catch {
      setPartnerDashboard(null);
    } finally {
      setPartnerDashboardLoading(false);
    }
  };

  useEffect(() => {
    const p = initialPartner;
    form.resetFields();
    const { dial, local } = splitDialAndLocal(p.mobile);
    form.setFieldsValue({
      name: p.name,
      companyName: p.companyName,
      websiteUrl: (p as any).websiteUrl || '',
      industry: (p as any).industry || '',
      email: p.email,
      phoneDialCode: dial,
      mobileNumber: local,
      country: p.country || undefined,
      status: p.status,
      commissionType: p.commissionType,
      commissionValue: Number(p.commissionValue),
      referralLink: p.referralLink || '',
      partnerLogoUrl: (p as AffiliatePartner).branding?.logos?.partner || '',
      partnerProgramServices: getPartnerProgramServicesForForm((p as AffiliatePartner).branding),
      partnerProgramFaqs: getPartnerProgramFaqsForForm((p as AffiliatePartner).branding),
      address: {
        street: (p as any).address?.street || '',
        apartment: (p as any).address?.apartment || '',
        city: (p as any).address?.city || '',
        state: (p as any).address?.state || '',
        zip: (p as any).address?.zip || '',
        country: (p as any).address?.country || '',
      },
      payment: {
        zelle: (p as any).paymentDetails?.zelle || '',
        venmo: (p as any).paymentDetails?.venmo || '',
        upi: (p as any).paymentDetails?.upi || '',
        bankBeneficiaryName: (p as any).paymentDetails?.bank?.beneficiaryName || '',
        bankName: (p as any).paymentDetails?.bank?.bankName || '',
        bankAccountNumber: (p as any).paymentDetails?.bank?.accountNumber || '',
        bankIfscSwift: (p as any).paymentDetails?.bank?.ifscSwift || '',
      },
    });
    fetchPartnerDashboard(p.partnerId);
    fetchPartnerKyc(p.partnerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per partner tab
  }, [initialPartner.partnerId]);

  const updateKycStatus = async (status: 'pending' | 'done' | 'deactivated') => {
    if (!editingPartner?.partnerId) return;
    try {
      const res: any = await callApi(
        `/affiliate/partners/${encodeURIComponent(editingPartner.partnerId)}/kyc/status`,
        'PATCH',
        { status }
      );
      if (!res?.status) {
        toastError(res?.data?.message || 'Failed to update KYC status');
        return;
      }
      const kycPayload = res?.data?.data;
      if (kycPayload?.partnerId != null && kycPayload?.kycStatus != null) {
        setPartnerKyc(kycPayload);
      }
      toastSuccess('KYC status updated');
      fetchPartnerKyc(editingPartner.partnerId);
      onSaved();
    } catch (error: any) {
      toastError(error?.message || 'Failed to update KYC status');
    }
  };

  const startPartnerKycWindow = async () => {
    if (!editingPartner?.partnerId) return;
    try {
      const res: any = await callApi(
        `/affiliate/partners/${encodeURIComponent(editingPartner.partnerId)}/kyc/restart-window`,
        'POST',
        null
      );
      if (!res?.status) {
        toastError(res?.data?.message || 'Failed to start KYC window');
        return;
      }
      toastSuccess('KYC submission window started. Partner account is active again.');
      fetchPartnerKyc(editingPartner.partnerId);
      onSaved();
    } catch (error: any) {
      toastError(error?.message || 'Failed to start KYC window');
    }
  };

  const updateDocumentReview = async (
    documentId: string,
    status: 'accepted' | 'rejected' | 'under_review',
    remark?: string
  ) => {
    if (!documentId) return;
    try {
      const res: any = await callApi(
        `/affiliate/partners/kyc/document/${encodeURIComponent(documentId)}/review`,
        'PATCH',
        { reviewStatus: status, reviewRemark: remark || '' }
      );
      if (!res?.status) {
        toastError(res?.data?.message || 'Failed to update document review');
        return;
      }
      toastSuccess('Document review updated');
      if (editingPartner?.partnerId) {
        fetchPartnerKyc(editingPartner.partnerId);
        onSaved();
      }
    } catch (error: any) {
      toastError(error?.message || 'Failed to update document review');
    }
  };

  const openPreviewModal = (doc: any) => {
    setPreviewDoc(doc);
    setPreviewRemark(doc?.reviewRemark || '');
    setPreviewModalOpen(true);
  };

  const handleUpdatePartner = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const mobile = joinDialAndLocal(values.phoneDialCode || '+1', values.mobileNumber || '');
      const payload = {
        name: values.name,
        companyName: values.companyName,
        websiteUrl: values.websiteUrl?.trim(),
        industry: values.industry?.trim(),
        email: values.email,
        mobile,
        country: values.country,
        status: values.status,
        commissionType: values.commissionType,
        commissionValue: Number(values.commissionValue || 0),
        address: {
          street: values.address?.street,
          apartment: values.address?.apartment,
          city: values.address?.city,
          state: values.address?.state,
          zip: values.address?.zip,
          country: values.address?.country,
        },
        paymentDetails: {
          zelle: values.payment?.zelle,
          venmo: values.payment?.venmo,
          upi: values.payment?.upi,
          bank: {
            beneficiaryName: values.payment?.bankBeneficiaryName,
            bankName: values.payment?.bankName,
            accountNumber: values.payment?.bankAccountNumber,
            ifscSwift: values.payment?.bankIfscSwift,
          },
        },
        ...(editingPartner.subdomain
          ? {
              branding: buildPartnerBrandingPayload({
                partnerLogoUrl: values.partnerLogoUrl ?? '',
                displayName: values.companyName || values.name,
                partnerProgramServices: values.partnerProgramServices,
                partnerProgramFaqs: values.partnerProgramFaqs,
              }),
            }
          : {}),
      };

      const encodedPartnerId = encodeURIComponent(editingPartner.partnerId);
      const res: any = await callApi(`${AFFILIATE_PARTNERS}/${encodedPartnerId}`, 'PATCH', payload);

      if (!res?.status) {
        toastError(res?.data?.message || 'Failed to update partner');
        return;
      }

      toastSuccess('partner updated successfully');

      // Update editingPartner with new values to reflect changes in UI
      setEditingPartner({
        ...editingPartner,
        name: values.name,
        companyName: values.companyName,
        websiteUrl: values.websiteUrl?.trim(),
        industry: values.industry?.trim(),
        email: values.email,
        mobile,
        country: values.country,
        status: values.status,
        commissionType: values.commissionType,
        commissionValue: Number(values.commissionValue || 0),
      });

      setPartnerInformationEditing(false);
      onSaved();
    } catch (error: any) {
      if (error?.errorFields) return;
      toastError(error?.message || 'Failed to update partner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePartner = async () => {
    try {
      const encodedPartnerId = encodeURIComponent(editingPartner.partnerId);
      const res: any = await callApi(`${AFFILIATE_PARTNERS}/${encodedPartnerId}`, 'DELETE', null);

      if (!res?.status) {
        toastError(res?.data?.message || 'Failed to delete partner');
        return;
      }

      toastSuccess('partner deleted successfully');
      onSaved();
      onClose();
    } catch (error: any) {
      toastError(error?.message || 'Failed to delete partner');
    }
  };

  const getPaymentTagColor = (status?: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('paid') || s.includes('success') || s.includes('complete')) return 'green';
    if (s.includes('pending') || s.includes('process')) return 'gold';
    if (s.includes('fail') || s.includes('cancel') || s.includes('reject')) return 'red';
    return 'blue';
  };

  const allPartnerOrders = partnerDashboard?.orders || [];

  const orderServiceOptions = useMemo(() => {
    const set = new Set<string>();
    allPartnerOrders.forEach(o => {
      const s = (o.serviceName || '').trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allPartnerOrders]);

  const orderPaymentOptions = useMemo(() => {
    const set = new Set<string>();
    allPartnerOrders.forEach(o => {
      const s = (o.paymentStatus || '').trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allPartnerOrders]);

  const matchesDatePreset = (createdAt: string | undefined, preset: string) => {
    if (preset === 'all') return true;
    if (!createdAt) return false;
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (preset === 'today') return d >= startOfToday;
    if (preset === 'week') return now.getTime() - d.getTime() <= 7 * 24 * 60 * 60 * 1000;
    if (preset === 'month') return now.getTime() - d.getTime() <= 30 * 24 * 60 * 60 * 1000;
    if (preset === 'year') return d.getFullYear() === now.getFullYear();
    return true;
  };

  const filteredPartnerOrders = useMemo(() => {
    return allPartnerOrders.filter(o => {
      if (orderSearch.trim()) {
        const q = orderSearch.toLowerCase().trim();
        const hay = [o.orderId, o.id, o.userName, o.userEmail, o.serviceName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterService !== '__all__') {
        if ((o.serviceName || '') !== filterService) return false;
      }
      if (filterPayment !== '__all__') {
        if ((o.paymentStatus || '') !== filterPayment) return false;
      }
      if (!matchesDatePreset(o.createdAt, filterDatePreset)) return false;
      return true;
    });
  }, [allPartnerOrders, orderSearch, filterService, filterPayment, filterDatePreset]);

  const buildOrderExportRows = () =>
    filteredPartnerOrders.map(o => ({
      'Order ID': o.orderId || o.id || '',
      'User Name': o.userName || '',
      'User Email': o.userEmail || '',
      'Service Name': o.serviceName || '',
      'Payment Status': o.paymentStatus || '',
      'Earning (USD)': Number(o.commission || 0),
      'Date of Submission': o.createdAt ? new Date(o.createdAt).toISOString().slice(0, 10) : '',
    }));

  const buildOrdersFileName = (ext: string) => {
    const partnerId = editingPartner?.partnerId || 'partner';
    const stamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, '')
      .slice(0, 14);
    return `${partnerId}_orders_${stamp}.${ext}`;
  };

  const handleDownloadOrdersExcel = () => {
    if (!filteredPartnerOrders.length) {
      toastWarning('No orders to download for the current filters.');
      return;
    }
    try {
      const rows = buildOrderExportRows();
      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Orders');
      XLSX.writeFile(book, buildOrdersFileName('xlsx'));
      toastSuccess('Orders exported to Excel');
    } catch (err) {
      toastError('Failed to export Excel');
    }
  };

  const handleDownloadOrdersPdf = () => {
    if (!filteredPartnerOrders.length) {
      toastWarning('No orders to download for the current filters.');
      return;
    }
    try {
      const rows = buildOrderExportRows();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const title = `Orders — ${editingPartner?.partnerId || 'Partner'}`;
      doc.setFontSize(14);
      doc.text(title, 40, 40);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString()}  |  Records: ${rows.length}`, 40, 58);

      const headers = Object.keys(rows[0] || {});
      const colWidths = [110, 95, 160, 120, 90, 70, 90];
      let y = 80;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      let x = 40;
      headers.forEach((h, i) => {
        doc.text(String(h), x, y);
        x += colWidths[i] || 100;
      });
      doc.setFont('helvetica', 'normal');
      y += 14;
      doc.setLineWidth(0.4);
      doc.line(40, y - 9, 40 + colWidths.reduce((a, b) => a + b, 0), y - 9);

      rows.forEach(row => {
        if (y > 540) {
          doc.addPage();
          y = 40;
        }
        let xi = 40;
        headers.forEach((h, i) => {
          const cell = String((row as any)[h] ?? '');
          const max = (colWidths[i] || 100) - 6;
          const truncated =
            doc.getTextWidth(cell) > max
              ? cell.slice(
                  0,
                  Math.max(3, Math.floor((max / doc.getTextWidth(cell)) * cell.length) - 1)
                ) + '…'
              : cell;
          doc.text(truncated, xi, y);
          xi += colWidths[i] || 100;
        });
        y += 14;
      });

      doc.save(buildOrdersFileName('pdf'));
      toastSuccess('Orders exported to PDF');
    } catch (err) {
      toastError('Failed to export PDF');
    }
  };

  const partnerOrderColumns: any[] = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 220,
      ellipsis: true,
      render: (value: string, row: PartnerOrderRow) => (
        <Text strong style={{ color: '#0f4c81', whiteSpace: 'nowrap' }}>
          {value || row.id || '--'}
        </Text>
      ),
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 280,
      ellipsis: true,
      render: (v: string) => v || '--',
    },
    {
      title: 'User Email',
      dataIndex: 'userEmail',
      key: 'userEmail',
      width: 260,
      ellipsis: true,
      render: (v: string) => v || '--',
    },
    {
      title: 'Service Name',
      dataIndex: 'serviceName',
      key: 'serviceName',
      width: 200,
      ellipsis: true,
      render: (v: string) => v || '--',
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 150,
      ellipsis: true,
      render: (v: string) => (
        <Tag color={getPaymentTagColor(v)} style={{ whiteSpace: 'nowrap' }}>
          {v || '—'}
        </Tag>
      ),
    },
    {
      title: 'Earning',
      dataIndex: 'commission',
      key: 'commission',
      width: 100,
      render: (v: number) => (
        <Text strong style={{ color: Number(v || 0) > 0 ? '#16a34a' : '#9ca3af' }}>
          {Number(v || 0) > 0 ? `$${Number(v).toLocaleString()}` : '-'}
        </Text>
      ),
    },
    {
      title: 'Date of Submission',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => (v ? new Date(v).toISOString().slice(0, 10) : '--'),
    },
  ];

  const partnerDialLocal = splitDialAndLocal(editingPartner.mobile);

  return (
    <>
      <GenericAccordian>
        <div className="affiliate-partner-edit users-form-layout">
          <Form form={form} layout="horizontal" className="affiliate-partner-edit__form">
            <HorizontalTabNavigation>
              <HorizontalTabPanel id="partner-information" label="Partner Information">
                <div
                  className="container1"
                  style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', padding: '20px' }}
                >
                  <UserFormSidebar
                    name={nameWatched || editingPartner.name}
                    agentId={editingPartner.partnerId}
                    companyName={companyNameWatched ?? editingPartner.companyName}
                    email={emailWatched || editingPartner.email}
                    phone={
                      phoneDialCodeWatched && mobileNumberWatched
                        ? `${phoneDialCodeWatched} ${mobileNumberWatched}`
                        : partnerDialLocal.dial && partnerDialLocal.local
                          ? `${partnerDialLocal.dial} ${partnerDialLocal.local}`
                          : editingPartner.mobile
                    }
                    country={countryWatched ?? editingPartner.country}
                    isActive={
                      (statusWatched || editingPartner.status || '').toLowerCase() === 'active'
                    }
                    isPartner
                  />
                  <div
                    className="form-container users-form-main partner-information-form-card"
                    style={{ flex: '1 1 500px', minWidth: 0 }}
                  >
                    <div className="partner-information-card-header" style={{ paddingTop: '0px' }}>
                      <div className="partner-information-card-title">
                        <Title level={5}>Partner Information</Title>
                        {(editingPartner.subdomain || editingPartner.referralLink) && (
                          <div
                            className="partner-information-referral-domain"
                            title="Referral Domain"
                          >
                            <span className="partner-information-referral-domain__label">
                              Referral Domain:
                            </span>
                            <span className="partner-information-referral-domain__value">
                              {editingPartner.subdomain
                                ? formatPartnerReferralDomain(editingPartner.subdomain)
                                : editingPartner.referralLink}
                            </span>
                            <Button
                              type="text"
                              size="small"
                              aria-label="Copy referral domain"
                              icon={<Copy size={15} />}
                              onClick={() => {
                                try {
                                  const link = editingPartner.subdomain
                                    ? buildPartnerSubdomainUrl(editingPartner.subdomain)
                                    : editingPartner.referralLink;
                                  navigator.clipboard.writeText(link);
                                  toastSuccess('Referral domain copied');
                                } catch {
                                  toastError('Unable to copy');
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <InvertFillButton
                        onClick={() => {
                          if (partnerInformationEditing) {
                            handleUpdatePartner();
                          } else {
                            setPartnerInformationEditing(true);
                          }
                        }}
                        type="button"
                        disabled={submitting}
                      >
                        {submitting ? (
                          'Updating...'
                        ) : partnerInformationEditing ? (
                          'Update'
                        ) : (
                          <span className="button-content">
                            <EditOutlined /> Edit
                          </span>
                        )}
                      </InvertFillButton>
                    </div>
                    {partnerInformationEditing ? (
                      <div className="users-form-grid">
                        <Form.Item
                          name="name"
                          label="Partner Name"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Partner name is required' }]}
                        >
                          <Input placeholder="Enter partner name" />
                        </Form.Item>
                        <Form.Item
                          name="companyName"
                          label={<div className="leftOffset">Company Name</div>}
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          colon={false}
                        >
                          <Input placeholder="Enter company name" />
                        </Form.Item>
                        <Form.Item
                          name="websiteUrl"
                          label={<div className="leftOffset">Website URL</div>}
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          colon={false}
                        >
                          <Input placeholder="Enter website URL" />
                        </Form.Item>
                        <Form.Item
                          name="industry"
                          label={<div className="leftOffset">Industry</div>}
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          colon={false}
                        >
                          <Input placeholder="Enter industry" />
                        </Form.Item>
                        <Form.Item
                          name="country"
                          label={<div className="leftOffset">Origin Country</div>}
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          colon={false}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select country"
                            dropdownClassName="select-checkbox-dropdown"
                            options={countries.map(c => ({ label: c, value: c }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name="email"
                          label="Email ID"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[
                            { required: true, message: 'Email is required' },
                            { type: 'email', message: 'Invalid email' },
                          ]}
                        >
                          <Input placeholder="Enter email address" />
                        </Form.Item>
                        <Form.Item
                          label={<div className="leftOffset">Mobile Number</div>}
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          colon={false}
                        >
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                              name="phoneDialCode"
                              noStyle
                              initialValue="+1"
                              rules={[{ required: true, message: 'Code is required' }]}
                            >
                              <Select
                                showSearch
                                optionFilterProp="label"
                                style={{ width: 160 }}
                                dropdownClassName="select-checkbox-dropdown"
                                options={PHONE_DIAL_CODES.map(d => ({
                                  label: d.label,
                                  value: d.value,
                                }))}
                              />
                            </Form.Item>
                            <Form.Item
                              name="mobileNumber"
                              noStyle
                              rules={[{ required: true, message: 'Mobile number is required' }]}
                            >
                              <Input
                                style={{ width: 'calc(100% - 160px)' }}
                                placeholder="Mobile number"
                              />
                            </Form.Item>
                          </Space.Compact>
                        </Form.Item>
                        <Form.Item
                          name="status"
                          label="Status"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Status is required' }]}
                        >
                          <Radio.Group>
                            <Radio value="active">Active</Radio>
                            <Radio value="inactive">Inactive</Radio>
                          </Radio.Group>
                        </Form.Item>
                        <Form.Item
                          name={['address', 'street']}
                          label="Address Line 1"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input placeholder="Enter street address" />
                        </Form.Item>
                        <Form.Item
                          name={['address', 'apartment']}
                          label={<div className="leftOffset">Address Line 2</div>}
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          colon={false}
                        >
                          <Input placeholder="Enter apartment, suite, etc. (optional)" />
                        </Form.Item>
                        <Form.Item
                          name={['address', 'city']}
                          label="City"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input placeholder="Enter city" />
                        </Form.Item>
                        <Form.Item
                          name={['address', 'state']}
                          label="State"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input placeholder="Enter state or province" />
                        </Form.Item>
                        <Form.Item
                          name={['address', 'country']}
                          label="Country"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select country"
                            dropdownClassName="select-checkbox-dropdown"
                            options={countries.map(c => ({ label: c, value: c }))}
                          />
                        </Form.Item>
                        <Form.Item
                          name={['address', 'zip']}
                          label="Zip/Postal Code"
                          wrapperCol={{ span: 8, offset: 4 }}
                          labelCol={{ span: 4 }}
                          labelAlign="left"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input placeholder="Enter ZIP or postal code" />
                        </Form.Item>
                      </div>
                    ) : (
                      <div className="partner-information-readonly">
                        {[
                          {
                            icon: Globe,
                            label: 'Website URL',
                            value: websiteUrlWatched ?? (editingPartner as any).websiteUrl,
                          },
                          {
                            icon: Briefcase,
                            label: 'Industry',
                            value: industryWatched ?? (editingPartner as any).industry,
                          },
                          {
                            icon: Home,
                            label: 'Address Line 1',
                            value: addressWatched?.street ?? partnerAddress.street,
                          },
                          {
                            icon: Building2,
                            label: 'Address Line 2',
                            value: addressWatched?.apartment ?? partnerAddress.apartment,
                          },
                          {
                            icon: MapPin,
                            label: 'City',
                            value: addressWatched?.city ?? partnerAddress.city,
                          },
                          {
                            icon: Map,
                            label: 'State',
                            value: addressWatched?.state ?? partnerAddress.state,
                          },
                          {
                            icon: Flag,
                            label: 'Country',
                            value: addressWatched?.country ?? partnerAddress.country,
                          },
                          {
                            icon: Hash,
                            label: 'Zip/Postal Code',
                            value: addressWatched?.zip ?? partnerAddress.zip,
                          },
                        ].map(({ icon: Icon, label, value }) => (
                          <div className="partner-information-readonly__row" key={label}>
                            <div className="partner-information-readonly__label-wrap">
                              <div className="partner-information-readonly__icon-box">
                                <Icon
                                  className="partner-information-readonly__icon"
                                  size={18}
                                  strokeWidth={2}
                                />
                              </div>
                              <span className="partner-information-readonly__label">{label}</span>
                            </div>
                            <span className="partner-information-readonly__value">
                              {value || '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </HorizontalTabPanel>

              <HorizontalTabPanel id="affiliate-settings" label="Affiliate Settings">
                <div style={{ border: '1px solid #e5e5e5', padding: 20, borderRadius: 8 }}>
                  <Form.Item
                    name="commissionType"
                    label="Commission Type"
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[{ required: true, message: 'Commission type is required' }]}
                  >
                    <Radio.Group>
                      <Radio value="percentage">Percentage (% of order amount)</Radio>
                      <Radio value="flat">Fixed amount (USD per qualifying order)</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name="commissionValue"
                    label={
                      commissionTypeWatched === 'flat'
                        ? 'Fixed commission (USD)'
                        : 'Commission rate (%)'
                    }
                    wrapperCol={{ span: 8, offset: 4 }}
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    rules={[
                      { required: true, message: 'Commission value is required' },
                      {
                        validator: async (_, value) => {
                          if (value === null || value === undefined || value === '') {
                            return Promise.reject(new Error('Commission value is required'));
                          }
                          const n = Number(value);
                          if (Number.isNaN(n) || n < 0) {
                            return Promise.reject(new Error('Enter zero or a positive number'));
                          }
                          if (commissionTypeWatched === 'percentage' && n > 100) {
                            return Promise.reject(
                              new Error('Percentage must be between 0 and 100')
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    extra={
                      commissionTypeWatched === 'flat'
                        ? 'Flat dollar amount earned per order (not a percent of the order).'
                        : 'Example: 10 means 10% of the order amount. Must be 0–100.'
                    }
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={commissionTypeWatched === 'percentage' ? 100 : undefined}
                      step={commissionTypeWatched === 'percentage' ? 0.1 : 0.01}
                      precision={2}
                      addonAfter={commissionTypeWatched === 'flat' ? undefined : '%'}
                      placeholder={commissionTypeWatched === 'flat' ? '0.00' : '0–100'}
                    />
                  </Form.Item>
                </div>
              </HorizontalTabPanel>

              {editingPartner.subdomain ? (
                <HorizontalTabPanel id="page-customize" label="Page Branding">
                  <div style={{ marginBottom: 12 }}>
                    <Accordian title="Partner Logo">
                      <Form.Item
                        name="partnerLogoUrl"
                        wrapperCol={{ span: 8, offset: 4 }}
                        labelCol={{ span: 4 }}
                        labelAlign="left"
                      >
                        <PartnerLogoUpload />
                      </Form.Item>
                    </Accordian>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Accordian title="Partner Program Services">
                      <PartnerProgramServicesEditor />
                    </Accordian>
                  </div>
                  <div>
                    <Accordian title="Partner Program FAQs">
                      <PartnerProgramFaqsEditor />
                    </Accordian>
                  </div>
                </HorizontalTabPanel>
              ) : null}

              <HorizontalTabPanel id="payment-information" label="Payment Information">
                <Form.Item
                  name={['payment', 'zelle']}
                  label={<div className="leftOffset">Zelle ID</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                >
                  <Input placeholder="Enter Zelle ID" />
                </Form.Item>
                <Form.Item
                  name={['payment', 'venmo']}
                  label={<div className="leftOffset">Venmo ID</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                >
                  <Input placeholder="Enter Venmo ID" />
                </Form.Item>
                <Form.Item
                  name={['payment', 'upi']}
                  label={<div className="leftOffset">UPI ID</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                  style={{ border: 'none' }}
                >
                  <Input placeholder="Enter UPI ID" />
                </Form.Item>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '20px',
                    marginLeft: '30px',
                    color: '#004195',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  <span>Bank Information</span>

                  <div
                    style={{
                      flex: 1,
                      borderTop: '1px solid #e2e8f0',
                    }}
                  />
                </div>
                <Form.Item
                  name={['payment', 'bankBeneficiaryName']}
                  label={<div className="leftOffset">Beneficiary Name</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                >
                  <Input placeholder="Enter beneficiary name" />
                </Form.Item>
                <Form.Item
                  name={['payment', 'bankName']}
                  label={<div className="leftOffset">Bank Name</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                >
                  <Input placeholder="Enter bank name" />
                </Form.Item>
                <Form.Item
                  name={['payment', 'bankAccountNumber']}
                  label={<div className="leftOffset">Account Number</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                >
                  <Input placeholder="Enter account number" />
                </Form.Item>
                <Form.Item
                  name={['payment', 'bankIfscSwift']}
                  label={<div className="leftOffset">Routing / IFSC / SWIFT</div>}
                  wrapperCol={{ span: 8, offset: 4 }}
                  labelCol={{ span: 4 }}
                  labelAlign="left"
                  colon={false}
                >
                  <Input placeholder="Enter routing/IFSC/SWIFT code" />
                </Form.Item>
              </HorizontalTabPanel>

              <HorizontalTabPanel id="kyc-documents" label="KYC Documents">
                <div style={{ border: '1px solid #e5e5e5', padding: 20, borderRadius: 8 }}>
                  <Spin spinning={partnerKycLoading}>
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: 12,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}
                        bodyStyle={{ padding: '14px 20px' }}
                      >
                        <Row gutter={[24, 12]}>
                          <Col xs={24} sm={8}>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              Status:
                            </Text>{' '}
                            <Text strong style={{ fontSize: 14 }}>
                              {String(partnerKyc?.kycStatus || 'pending').toUpperCase()}
                            </Text>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              Due Date:
                            </Text>{' '}
                            <Text strong style={{ fontSize: 14 }}>
                              {partnerKyc?.kycDueAt
                                ? new Date(partnerKyc.kycDueAt).toLocaleDateString()
                                : '--'}
                            </Text>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              Uploaded:
                            </Text>{' '}
                            <Text strong style={{ fontSize: 14 }}>
                              {Number(partnerKyc?.uploadedCount || 0)}/
                              {Number(partnerKyc?.requiredCount || 6)}
                            </Text>
                          </Col>
                        </Row>
                      </Card>
                      <Row gutter={[10, 10]}>
                        {(partnerKyc?.documents || []).map((row: any) => {
                          const status = String(row?.reviewStatus || 'under_review').toLowerCase();
                          const cardBackground =
                            status === 'accepted'
                              ? '#90EE90'
                              : status === 'rejected'
                                ? '#fb8775'
                                : row?.reviewRemark
                                  ? '#fffbe6'
                                  : '#ffffff';
                          const statusText =
                            status === 'accepted'
                              ? 'Accepted'
                              : status === 'rejected'
                                ? 'Rejected'
                                : 'Under Review';
                          return (
                            <Col xs={24} sm={12} md={6} key={row?.id || row?.documentType}>
                              <Card
                                size="small"
                                bodyStyle={{ padding: 9 }}
                                style={{
                                  height: '100%',
                                  borderRadius: 12,
                                  backgroundColor: cardBackground,
                                  border: '1px solid #d9d9d9',
                                }}
                                hoverable
                              >
                                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                                  <Text strong style={{ fontSize: 12 }}>
                                    {row?.documentType || 'Document'}
                                  </Text>

                                  {row?.fileUrl ? (
                                    <div
                                      onClick={() => openPreviewModal(row)}
                                      style={{
                                        width: '100%',
                                        aspectRatio: '1',
                                        border: '1px solid #e8e8e8',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        background: '#fafafa',
                                        maxHeight: 160,
                                        position: 'relative',
                                      }}
                                      title="Click to open full preview"
                                    >
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '10px',
                                          left: '10px',
                                          padding: '4px 10px',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          color:
                                            status === 'accepted'
                                              ? 'white'
                                              : status === 'rejected'
                                                ? 'white'
                                                : '#475569',
                                          backgroundColor:
                                            status === 'accepted'
                                              ? '#28a745'
                                              : status === 'rejected'
                                                ? '#dc3545'
                                                : '#ffffff',
                                          border:
                                            status === 'accepted' || status === 'rejected'
                                              ? 'none'
                                              : '1px solid #323232',
                                          zIndex: 10,
                                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        }}
                                      >
                                        {status === 'accepted'
                                          ? 'Accepted'
                                          : status === 'rejected'
                                            ? 'Rejected'
                                            : 'Under Review'}
                                      </div>
                                      <iframe
                                        title={`thumbnail-${row?.id || row?.fileName || 'doc'}`}
                                        src={row.fileUrl}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          border: 0,
                                          overflow: 'hidden',
                                          pointerEvents: 'none',
                                        }}
                                        scrolling="no"
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        aspectRatio: '1',
                                        border: '1px dashed #d9d9d9',
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#fafafa',
                                        position: 'relative',
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: 'absolute',
                                          top: '10px',
                                          left: '10px',
                                          padding: '4px 10px',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          fontWeight: 600,
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          color:
                                            status === 'accepted'
                                              ? 'white'
                                              : status === 'rejected'
                                                ? 'white'
                                                : '#475569',
                                          backgroundColor:
                                            status === 'accepted'
                                              ? '#28a745'
                                              : status === 'rejected'
                                                ? '#dc3545'
                                                : '#ffffff',
                                          border:
                                            status === 'accepted' || status === 'rejected'
                                              ? 'none'
                                              : '1px solid #323232',
                                          zIndex: 10,
                                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        }}
                                      >
                                        {status === 'accepted'
                                          ? 'Accepted'
                                          : status === 'rejected'
                                            ? 'Rejected'
                                            : 'Under Review'}
                                      </div>
                                      <Text type="secondary" style={{ fontSize: 10 }}>
                                        No thumbnail available
                                      </Text>
                                    </div>
                                  )}

                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      placeItems: 'center',
                                      gap: 8,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: '#666',
                                        wordBreak: 'break-word',
                                        flex: 1,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {row?.fileName || 'Untitled'}
                                    </Text>
                                    <Space size={4}>
                                      <Tooltip
                                        placement="top"
                                        title={'Update review status with remark'}
                                      >
                                        <Button
                                          size="small"
                                          onClick={() => openPreviewModal(row)}
                                          // style={{
                                          //   color: '#075dc5',
                                          //   border: 'none',
                                          //   backgroundColor: 'transparent',
                                          //   padding: '3px 8px',
                                          //   fontSize: '11px',
                                          //   height: '28px',
                                          //   fontWeight: 600,
                                          //   whiteSpace: 'nowrap',
                                          // }}
                                        >
                                          Add Remark
                                        </Button>
                                      </Tooltip>
                                      <Tooltip placement="top" title={'Accept'}>
                                        <Button
                                          size="small"
                                          style={{
                                            color: '#28a745',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            padding: '3px 8px',
                                            fontSize: '13px',
                                            height: '28px',
                                            width: '100%',
                                          }}
                                          onClick={() =>
                                            updateDocumentReview(
                                              row.id,
                                              'accepted',
                                              row?.reviewRemark || ''
                                            )
                                          }
                                        >
                                          <CircleCheckBig size={20} />
                                        </Button>
                                      </Tooltip>
                                      <Tooltip placement="top" title={'Reject'}>
                                        <Button
                                          size="small"
                                          style={{
                                            color: '#dc3545',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            padding: '3px 8px',
                                            fontSize: '13px',
                                            height: '28px',
                                            width: '100%',
                                          }}
                                          onClick={() =>
                                            updateDocumentReview(
                                              row.id,
                                              'rejected',
                                              row?.reviewRemark || ''
                                            )
                                          }
                                        >
                                          <CircleX size={20} />
                                        </Button>
                                      </Tooltip>
                                    </Space>
                                  </div>

                                  <Text
                                    style={{
                                      fontSize: 14,
                                      color: '#666',
                                      display: 'block',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    Uploaded:{' '}
                                    {row?.uploadedAt
                                      ? new Date(row.uploadedAt).toLocaleDateString()
                                      : 'N/A'}
                                  </Text>
                                  <Text
                                    style={{
                                      fontSize: 14,
                                      color: '#666',
                                      display: 'block',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    Remark: {row?.reviewRemark || 'N/A'}
                                  </Text>
                                </Space>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                      <Space wrap>
                        <InvertFillButton variant="blue" onClick={startPartnerKycWindow}>
                          Start KYC submission window
                        </InvertFillButton>
                        <InvertFillButton variant="white" onClick={() => updateKycStatus('done')}>
                          Mark KYC Done
                        </InvertFillButton>
                      </Space>
                    </Space>
                  </Spin>
                </div>
              </HorizontalTabPanel>

              <HorizontalTabPanel
                id="performance-overview"
                label="Performance Overview & Order Details"
              >
                <div style={{ border: '1px solid #e5e5e5', padding: 20, borderRadius: 8 }}>
                  <div style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <Title level={4} style={{ marginBottom: 4 }}>
                          Total Orders
                        </Title>
                        <Text type="secondary" style={{ fontSize: 14, color: '#795548' }}>
                          {allPartnerOrders.length} orders in total
                          {filteredPartnerOrders.length !== allPartnerOrders.length
                            ? ` · ${filteredPartnerOrders.length} shown`
                            : ''}
                        </Text>
                      </div>
                      <Space>
                        <Button
                          icon={<FileExcelOutlined style={{ color: '#1d6f42' }} />}
                          style={{
                            borderRadius: 8,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          onClick={handleDownloadOrdersExcel}
                        >
                          Excel
                        </Button>
                        <Button
                          icon={<FilePdfOutlined style={{ color: '#ff4d4f' }} />}
                          style={{
                            borderRadius: 8,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                          onClick={handleDownloadOrdersPdf}
                        >
                          PDF
                        </Button>
                      </Space>
                    </div>
                    <Row gutter={[12, 12]} style={{ marginTop: 16 }} align="middle" wrap>
                      <Col flex="1 1 280px">
                        <Input
                          allowClear
                          placeholder="Search by Order ID, User Name, Email, or Service..."
                          value={orderSearch}
                          onChange={e => setOrderSearch(e.target.value)}
                          prefix={<SearchOutlined style={{ color: '#9e9e9e' }} />}
                          style={{
                            background: '#fff9f5',
                            borderRadius: 8,
                            height: 40,
                          }}
                        />
                      </Col>
                      <Col flex="0 1 160px">
                        <Select
                          className="b2b-filter-select"
                          value={filterService}
                          onChange={setFilterService}
                          style={{ width: '100%', marginBottom: '8px' }}
                          showSearch
                          optionFilterProp="label"
                          optionLabelProp="displayLabel"
                          menuItemSelectedIcon={null}
                          options={(() => {
                            const arr = [
                              { label: 'All Services', value: '__all__' },
                              ...orderServiceOptions.map(s => ({ label: s, value: s })),
                            ];
                            return arr.map(opt => {
                              const labelText = String(opt.label ?? opt.value ?? '');
                              const isSelected = String(filterService) === String(opt.value);
                              return {
                                ...opt,
                                displayLabel: labelText,
                                label: (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '2px 0',
                                      fontSize: '14px',
                                      fontWeight: isSelected ? 600 : 500,
                                      color: isSelected ? '#075dc5' : '#1f2937',
                                      lineHeight: 1.2,
                                      width: '100%',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '3px',
                                        border: isSelected
                                          ? '2px solid #075dc5'
                                          : '1.5px solid #94a3b8',
                                        backgroundColor: isSelected ? '#075dc5' : '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition:
                                          'background-color 0.15s ease, border-color 0.15s ease',
                                        boxShadow: isSelected
                                          ? '0 1px 3px rgba(7, 93, 197, 0.3)'
                                          : 'none',
                                      }}
                                    >
                                      {isSelected && (
                                        <Check
                                          size={11}
                                          strokeWidth={3.5}
                                          color="#ffffff"
                                          style={{ display: 'block' }}
                                        />
                                      )}
                                    </div>
                                    <span
                                      style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {labelText}
                                    </span>
                                  </div>
                                ),
                              };
                            });
                          })()}
                        />
                      </Col>
                      <Col flex="0 1 160px">
                        <Select
                          className="b2b-filter-select"
                          value={filterPayment}
                          onChange={setFilterPayment}
                          style={{ width: '100%', marginBottom: '8px' }}
                          showSearch
                          optionFilterProp="label"
                          optionLabelProp="displayLabel"
                          menuItemSelectedIcon={null}
                          options={(() => {
                            const arr = [
                              { label: 'All Payment', value: '__all__' },
                              ...orderPaymentOptions.map(s => ({ label: s, value: s })),
                            ];
                            return arr.map(opt => {
                              const labelText = String(opt.label ?? opt.value ?? '');
                              const isSelected = String(filterPayment) === String(opt.value);
                              return {
                                ...opt,
                                displayLabel: labelText,
                                label: (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '2px 0',
                                      fontSize: '14px',
                                      fontWeight: isSelected ? 600 : 500,
                                      color: isSelected ? '#075dc5' : '#1f2937',
                                      lineHeight: 1.2,
                                      width: '100%',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '3px',
                                        border: isSelected
                                          ? '2px solid #075dc5'
                                          : '1.5px solid #94a3b8',
                                        backgroundColor: isSelected ? '#075dc5' : '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition:
                                          'background-color 0.15s ease, border-color 0.15s ease',
                                        boxShadow: isSelected
                                          ? '0 1px 3px rgba(7, 93, 197, 0.3)'
                                          : 'none',
                                      }}
                                    >
                                      {isSelected && (
                                        <Check
                                          size={11}
                                          strokeWidth={3.5}
                                          color="#ffffff"
                                          style={{ display: 'block' }}
                                        />
                                      )}
                                    </div>
                                    <span
                                      style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {labelText}
                                    </span>
                                  </div>
                                ),
                              };
                            });
                          })()}
                        />
                      </Col>
                      <Col flex="0 1 160px">
                        <Select
                          className="b2b-filter-select"
                          value={filterDatePreset}
                          onChange={setFilterDatePreset}
                          style={{ width: '100%', marginBottom: '8px' }}
                          showSearch
                          optionFilterProp="label"
                          optionLabelProp="displayLabel"
                          menuItemSelectedIcon={null}
                          options={(() => {
                            const arr = [
                              { label: 'All Dates', value: 'all' },
                              { label: 'Today', value: 'today' },
                              { label: 'Last 7 days', value: 'week' },
                              { label: 'Last 30 days', value: 'month' },
                              { label: 'This year', value: 'year' },
                            ];
                            return arr.map(opt => {
                              const labelText = String(opt.label ?? opt.value ?? '');
                              const isSelected = String(filterDatePreset) === String(opt.value);
                              return {
                                ...opt,
                                displayLabel: labelText,
                                label: (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '2px 0',
                                      fontSize: '14px',
                                      fontWeight: isSelected ? 600 : 500,
                                      color: isSelected ? '#075dc5' : '#1f2937',
                                      lineHeight: 1.2,
                                      width: '100%',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '3px',
                                        border: isSelected
                                          ? '2px solid #075dc5'
                                          : '1.5px solid #94a3b8',
                                        backgroundColor: isSelected ? '#075dc5' : '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition:
                                          'background-color 0.15s ease, border-color 0.15s ease',
                                        boxShadow: isSelected
                                          ? '0 1px 3px rgba(7, 93, 197, 0.3)'
                                          : 'none',
                                      }}
                                    >
                                      {isSelected && (
                                        <Check
                                          size={11}
                                          strokeWidth={3.5}
                                          color="#ffffff"
                                          style={{ display: 'block' }}
                                        />
                                      )}
                                    </div>
                                    <span
                                      style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {labelText}
                                    </span>
                                  </div>
                                ),
                              };
                            });
                          })()}
                        />
                      </Col>
                    </Row>
                  </div>

                  <div>
                    <Text strong>ORDER DETAILS</Text>
                    <div style={{ marginTop: 10 }}>
                      <Table
                        className="affiliate-orders-table"
                        rowKey={(row: PartnerOrderRow) =>
                          `${row.id || 'na'}-${row.orderId || 'na'}-${row.createdAt || 'na'}-${row.userEmail || 'na'}`
                        }
                        columns={partnerOrderColumns}
                        dataSource={filteredPartnerOrders}
                        loading={partnerDashboardLoading}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1360 }}
                        locale={{ emptyText: 'No orders match your filters.' }}
                        bordered
                      />
                    </div>
                  </div>
                </div>
              </HorizontalTabPanel>
            </HorizontalTabNavigation>
          </Form>

          <div className="affiliate-partner-edit__actions">
            <InvertFillButton
              variant="blue"
              size="md"
              fontWeight="semibold"
              disabled={submitting}
              onClick={handleUpdatePartner}
            >
              Save Changes
            </InvertFillButton>
            <InvertFillButton
              variant="red"
              size="md"
              fontWeight="semibold"
              disabled={submitting}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </InvertFillButton>
            <InvertFillButton variant="white" size="md" fontWeight="semibold" onClick={onClose}>
              Cancel
            </InvertFillButton>
          </div>
        </div>
      </GenericAccordian>

      <ConfirmationModal
        open={deleteModalOpen}
        message="Are you sure you want to delete this partner?"
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDeletePartner();
        }}
        onCancel={() => setDeleteModalOpen(false)}
        onClose={() => setDeleteModalOpen(false)}
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>KYC Document Preview</span>
            <Tag
              style={{
                margin: 0,
                padding: '2px 10px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color:
                  String(previewDoc?.reviewStatus || 'under_review').toLowerCase() === 'accepted'
                    ? 'white'
                    : String(previewDoc?.reviewStatus || 'under_review').toLowerCase() ===
                        'rejected'
                      ? 'white'
                      : '#475569',
                backgroundColor:
                  String(previewDoc?.reviewStatus || 'under_review').toLowerCase() === 'accepted'
                    ? '#28a745'
                    : String(previewDoc?.reviewStatus || 'under_review').toLowerCase() ===
                        'rejected'
                      ? '#dc3545'
                      : '#ffffff',
                border:
                  String(previewDoc?.reviewStatus || 'under_review').toLowerCase() === 'accepted' ||
                  String(previewDoc?.reviewStatus || 'under_review').toLowerCase() === 'rejected'
                    ? '1px solid transparent'
                    : '1px solid #323232',
              }}
            >
              {String(previewDoc?.reviewStatus || 'under_review').replaceAll('_', ' ')}
            </Tag>
          </div>
        }
        open={previewModalOpen}
        onCancel={() => {
          setPreviewModalOpen(false);
          setPreviewDoc(null);
        }}
        centered
        footer={[
          <Space key="footer-space" size={5}>
            <InvertFillButton
              key="reject"
              variant="red"
              size="md"
              fontWeight="semibold"
              onClick={() => {
                updateDocumentReview(previewDoc?.id, 'rejected', previewRemark);
                setPreviewModalOpen(false);
              }}
              disabled={!previewDoc?.id}
            >
              Reject
            </InvertFillButton>
            <InvertFillButton
              key="accept"
              variant="blue"
              size="md"
              fontWeight="semibold"
              onClick={() => {
                updateDocumentReview(previewDoc?.id, 'accepted', previewRemark);
                setPreviewModalOpen(false);
              }}
              disabled={!previewDoc?.id}
            >
              Accept
            </InvertFillButton>

            <InvertFillButton
              key="save-remark"
              variant="white"
              size="md"
              fontWeight="semibold"
              onClick={() => {
                updateDocumentReview(
                  previewDoc?.id,
                  previewDoc?.reviewStatus || 'under_review',
                  previewRemark
                );
                setPreviewModalOpen(false);
              }}
              disabled={!previewDoc?.id}
            >
              Save Remark
            </InvertFillButton>
          </Space>,
        ]}
        width={800}
        bodyStyle={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '400px',
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <Text>
            <Text strong>Document:</Text> {previewDoc?.documentType || '--'}
          </Text>
          {/* <Text>
            <Text strong>Current Status:</Text>{' '}
            {String(previewDoc?.reviewStatus || 'under_review').replaceAll('_', ' ')}
          </Text> */}
          {previewDoc?.fileUrl ? (
            <iframe
              title="KYC preview modal"
              src={previewDoc.fileUrl}
              style={{
                width: '100%',
                height: '300px',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
              }}
            />
          ) : (
            <Text type="secondary">Preview is not available for this document.</Text>
          )}
          <div>
            <Text type="secondary">Remark</Text>
            <Input.TextArea
              rows={3}
              value={previewRemark}
              onChange={e => setPreviewRemark(e.target.value)}
              placeholder="Add review remark for partner"
              style={{ marginTop: 6 }}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default B2BAffiliatePartnerEditTab;
